<script setup>
// Dropdown custom (não-nativo) com a identidade visual do Chatwoot: trigger no
// estilo do components-next/select/Select.vue e lista flutuante no estilo do
// dropdown-menu/base/DropdownBody.vue. Substitui o <select> nativo (cuja lista
// aberta é renderizada pelo SO e não segue o tema). v-model + evento `change`.
import { ref, computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  options: {
    type: Array,
    default: () => [],
    validator: opts =>
      opts.every(o => typeof o === 'object' && 'value' in o && 'label' in o),
  },
  placeholder: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  // alinhamento vertical do menu: 'bottom' (padrão) abre pra baixo, 'top' pra cima
  align: { type: String, default: 'bottom' },
});
const emit = defineEmits(['change']);

const modelValue = defineModel({
  type: [String, Number, Boolean],
  default: '',
});

const isOpen = ref(false);

const sameValue = (a, b) => String(a) === String(b);
const selected = computed(() =>
  props.options.find(o => sameValue(o.value, modelValue.value))
);
const triggerLabel = computed(() => selected.value?.label ?? props.placeholder);

const toggle = () => {
  if (!props.disabled) isOpen.value = !isOpen.value;
};
const choose = option => {
  if (option.disabled) return;
  const changed = !sameValue(option.value, modelValue.value);
  modelValue.value = option.value;
  isOpen.value = false;
  if (changed) emit('change', option.value);
};
</script>

<template>
  <div v-on-clickaway="() => (isOpen = false)" class="relative w-full">
    <button
      type="button"
      :disabled="disabled"
      class="flex items-center justify-between w-full gap-2 px-3 py-2 text-sm text-left transition-all duration-200 rounded-lg outline outline-1 -outline-offset-1 bg-n-alpha-1 text-n-slate-12 disabled:opacity-60 disabled:cursor-not-allowed"
      :class="
        isOpen ? 'outline-n-blue-9' : 'outline-n-weak hover:outline-n-slate-6'
      "
      @click="toggle"
    >
      <span class="truncate" :class="{ 'text-n-slate-10': !selected }">
        {{ triggerLabel }}
      </span>
      <Icon
        icon="i-lucide-chevron-down"
        class="transition-transform shrink-0 size-4 text-n-slate-11"
        :class="{ 'rotate-180': isOpen }"
      />
    </button>

    <ul
      v-if="isOpen"
      class="absolute left-0 right-0 z-40 grid gap-1 px-2 py-2 overflow-y-auto text-sm list-none border shadow-sm bg-n-alpha-3 backdrop-blur-[100px] rounded-xl border-n-weak max-h-60"
      :class="align === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'"
    >
      <li v-for="option in options" :key="String(option.value)">
        <button
          type="button"
          :disabled="option.disabled"
          class="flex items-center justify-between w-full gap-2 px-2.5 py-1.5 text-left rounded-lg text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50 disabled:cursor-not-allowed"
          :class="{ 'bg-n-alpha-2': sameValue(option.value, modelValue) }"
          @click="choose(option)"
        >
          <span class="truncate">{{ option.label }}</span>
          <Icon
            v-if="sameValue(option.value, modelValue)"
            icon="i-lucide-check"
            class="shrink-0 size-4 text-n-blue-11"
          />
        </button>
      </li>
    </ul>
  </div>
</template>
